import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { Banner, Button, Container, NotificationDispatch, type SelectOption, Title } from '@tryhackme/thm-ui-components';
import { useEffect, useMemo, useState } from 'react';
import { SEO } from 'src/common/components/seo';
import { FormProvider, useFieldArray, useForm } from 'src/common/form';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import { NetworkConnections } from './components/connections';
import { NetworkNodes } from './components/nodes';
import { useGetNetworkConfigurationsQuery, useSaveNetworkConfigurationsMutation } from './networks.slice';
import { StyledButtonWrapper, StyledFontAwesomeIcon, StyledForm, StyledFormSelect, StyledLabel, StyledNetworkHeaderActionButtons, StyledNetworkHeaderNetworkInformation, StyledNetworkHeaderSaveActionButton, StyledNetworksHeader, StyledSubmitButtonWrapper, StyledSubnetLabel } from './networks.styles';
import type { FormValues } from './networks.types';
import { ReactComponent as BannerImage } from './svg/banner.svg';
export const AdminNetworks = () => {
  const {
    data
  } = useGetNetworkConfigurationsQuery();
  const [selectedNetworkIndex, setSelectedNetworkIndex] = useState<number>(0);
  const [saveNetworksData, {
    isLoading
  }] = useSaveNetworkConfigurationsMutation();
  const networkData = data?.data;
  const selectNetworkOptions: SelectOption[] = networkData?.map((network, index) => ({
    id: index.toString(),
    label: network.title,
    value: network.title
  })) ?? [{
    id: '0',
    label: 'Loading...',
    value: 'Loading...'
  }];
  const selectedNetwork = useMemo(() => {
    if (!networkData) {
      return null;
    }
    return networkData[selectedNetworkIndex];
  }, [networkData, selectedNetworkIndex]);
  const nodes = selectedNetwork?.nodes;
  const connections = selectedNetwork?.connections;
  const defaultValues = {
    nodes,
    connections
  };
  const formMethods = useForm<FormValues>({
    defaultValues
  });
  const {
    handleSubmit,
    control,
    reset
  } = formMethods;
  const {
    fields,
    append,
    remove
  } = useFieldArray({
    control,
    name: 'connections'
  });
  useEffect(() => {
    reset({
      connections,
      nodes
    });
  }, [connections, nodes, reset]);
  const handleSelection = (option?: SelectOption) => {
    if (option?.label) {
      const network = networkData?.find(b => b.title === option.label);
      if (network && networkData) setSelectedNetworkIndex(networkData.indexOf(network));
    }
  };
  const handleOnSave = async (networksData: FormValues) => {
    try {
      await saveNetworksData({
        title: selectedNetwork?.title ?? '',
        networkId: selectedNetwork?._id ?? '',
        subnetId: selectedNetwork?.aws?.subnet?.id ?? '',
        nodes: networksData.nodes,
        connections: networksData.connections
      }).unwrap();
      NotificationDispatch('success', 'Networks saved successfully');
    } catch (error) {
      if (isQueryErrorWithMessage(error)) {
        NotificationDispatch('error', error.data.message);
      } else {
        NotificationDispatch('error', 'An unknown error has occurred');
      }
    }
  };
  return <section data-sentry-component="AdminNetworks" data-sentry-source-file="networks.tsx">
      <SEO title="Admin Networks" description="Networks page" data-sentry-element="SEO" data-sentry-source-file="networks.tsx" />
      <Banner Image={BannerImage} top="-10rem" right="0" data-sentry-element="Banner" data-sentry-source-file="networks.tsx">
        <Title titleText="Networks" subTitleText="Managing TryHackMe Networks" isInBanner data-sentry-element="Title" data-sentry-source-file="networks.tsx" />
      </Banner>
      <Container data-sentry-element="Container" data-sentry-source-file="networks.tsx">
        <FormProvider {...formMethods} data-sentry-element="FormProvider" data-sentry-source-file="networks.tsx">
          {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
          <StyledForm onSubmit={handleSubmit(handleOnSave)} data-sentry-element="StyledForm" data-sentry-source-file="networks.tsx">
            <StyledNetworksHeader data-sentry-element="StyledNetworksHeader" data-sentry-source-file="networks.tsx">
              <StyledNetworkHeaderNetworkInformation data-sentry-element="StyledNetworkHeaderNetworkInformation" data-sentry-source-file="networks.tsx">
                <StyledLabel data-sentry-element="StyledLabel" data-sentry-source-file="networks.tsx">Network ID</StyledLabel>
                <StyledButtonWrapper data-sentry-element="StyledButtonWrapper" data-sentry-source-file="networks.tsx">
                  <StyledFormSelect options={selectNetworkOptions} handleSelection={handleSelection} selected={selectNetworkOptions[selectedNetworkIndex]} isSearchable={false} $width="30rem" data-sentry-element="StyledFormSelect" data-sentry-source-file="networks.tsx" />
                </StyledButtonWrapper>
                <StyledSubnetLabel data-sentry-element="StyledSubnetLabel" data-sentry-source-file="networks.tsx">Subnet ID: {selectedNetwork?.aws.subnet.id}</StyledSubnetLabel>
              </StyledNetworkHeaderNetworkInformation>
              <StyledNetworkHeaderActionButtons data-sentry-element="StyledNetworkHeaderActionButtons" data-sentry-source-file="networks.tsx">
                <Button variant="solid" color="add" onClick={() => append({
                from: 0,
                to: 0
              })} data-testid="addConnectionTop" data-sentry-element="Button" data-sentry-source-file="networks.tsx">
                  <StyledFontAwesomeIcon icon={faPlus} data-sentry-element="StyledFontAwesomeIcon" data-sentry-source-file="networks.tsx" />
                  Add Connection
                </Button>
                <StyledNetworkHeaderSaveActionButton data-sentry-element="StyledNetworkHeaderSaveActionButton" data-sentry-source-file="networks.tsx">
                  <Button variant="solid" color="primary" type="submit" isDisabled={isLoading} data-sentry-element="Button" data-sentry-source-file="networks.tsx">
                    Save
                  </Button>
                </StyledNetworkHeaderSaveActionButton>
              </StyledNetworkHeaderActionButtons>
            </StyledNetworksHeader>
            <NetworkNodes data-sentry-element="NetworkNodes" data-sentry-source-file="networks.tsx" />
            <NetworkConnections {...{
            fields,
            append,
            remove
          }} data-sentry-element="NetworkConnections" data-sentry-source-file="networks.tsx" />
            <StyledSubmitButtonWrapper $mt="1.2rem" $mb="6rem" data-sentry-element="StyledSubmitButtonWrapper" data-sentry-source-file="networks.tsx">
              <Button variant="solid" color="primary" type="submit" isDisabled={isLoading} data-sentry-element="Button" data-sentry-source-file="networks.tsx">
                Save
              </Button>
            </StyledSubmitButtonWrapper>
          </StyledForm>
        </FormProvider>
      </Container>
    </section>;
};